import { z } from 'zod';
import type { MarketDataResponse, MarketDataSnapshot } from './marketData';
import { marketDataResponseSchema } from './marketData.schema';

export type TradeXRadarResponse = MarketDataResponse;
export interface TradeXRadarRealtimeMessage {
  version?: number;
  generatedAt?: string;
  streamId?: string;
  data: TradeXRadarResponse;
}
const metadataSchema = z.object({
  version: z.number().int().nonnegative().max(Number.MAX_SAFE_INTEGER).optional(),
  generatedAt: z.string().datetime({ offset: true }).optional(),
  streamId: z.string().uuid().optional(),
});
const responseSchema = marketDataResponseSchema.and(metadataSchema);
const messageSchema = z.union([
  metadataSchema.extend({ data: responseSchema }), responseSchema,
]);

export function normalizeTradeXRadar(value: unknown): MarketDataSnapshot {
  const message = messageSchema.parse(typeof value === 'string' ? JSON.parse(value) : value);
  const data = 'data' in message ? message.data : message;
  const sequence = message.version ?? data.version;
  const generatedAt = message.generatedAt ?? data.generatedAt;
  const streamId = message.streamId ?? data.streamId;
  const fetchedAt = Date.now();
  const evaluation = data.results.reduce(
    (latest, item) => Math.max(latest, Date.parse(item.evaluatedAtUtc)), 0,
  );
  const lastUpdated = generatedAt ?? new Date(evaluation || fetchedAt).toISOString();
  return {
    data, sequence, streamId, generatedAt, lastUpdated, fetchedAt,
    version: sequence === undefined ? lastUpdated : `${streamId ?? ''}:${sequence}`,
    etag: null, lastModified: null,
  };
}

// Compare at cache commit time, including HTTP responses racing with realtime messages.
export function newestSnapshot(
  current: MarketDataSnapshot | undefined, incoming: MarketDataSnapshot,
): MarketDataSnapshot {
  if (!current) return incoming;
  if (current.streamId === incoming.streamId &&
      current.sequence !== undefined && incoming.sequence !== undefined) {
    return incoming.sequence > current.sequence ? incoming : current;
  }
  // Backend restarts reset the sequence; generation time orders different streams.
  if (Date.parse(incoming.lastUpdated) <= Date.parse(current.lastUpdated)) return current;
  if (current.streamId === incoming.streamId) {
    const currentByKey = new Map(
      current.data.results.map((item) => [item.tradeInstrument.instrumentKey, item]),
    );
    const results = incoming.data.results.map((item) => {
      const previous = currentByKey.get(item.tradeInstrument.instrumentKey);
      return previous && JSON.stringify(previous) === JSON.stringify(item) ? previous : item;
    });
    if (results.some((item, index) => item !== incoming.data.results[index])) {
      incoming = {
        ...incoming,
        data: { ...incoming.data, results },
      };
    }
  }
  return incoming;
}
